import { t } from '@lingui/core/macro'
import { ExpeditionAttachmentItem, ExpeditionAttachmentPageFormat, ExpeditionAttachmentType } from '@typings/entities/Expedition'
import { fileFormUpload } from '@queries/multipartFormDataUpload'
import { displayToast } from '@utils/toast'
import { extractViolationMessage } from '@utils/extractViolationMessage'

type UploadAttachmentData = {
  expedition: string
  type: ExpeditionAttachmentType
  pageFormat: ExpeditionAttachmentPageFormat
  printPages: number
}

type ExpeditionAttachmentUploadType = (file: File, data: UploadAttachmentData) => Promise<Response>

export const expeditionAttachmentUpload: ExpeditionAttachmentUploadType = (file, data) => {
  const formData = new FormData()
  formData.append('data', JSON.stringify(data))
  return fileFormUpload('expedition-attachment/upload', file, formData)
}

type UploadPendingAttachmentsProps = {
  expeditionId: string
  attachments?: ExpeditionAttachmentItem[]
}

type UploadPendingAttachmentsType = (props: UploadPendingAttachmentsProps) => Promise<void>

type FailedAttachment = {
  name: string
  message?: string
}

const formatFailedAttachment = ({ name, message }: FailedAttachment): string => (message ? `${name} - ${message}` : name)

export const uploadPendingAttachments: UploadPendingAttachmentsType = async ({ expeditionId, attachments = [] }) => {
  const failed: FailedAttachment[] = []
  for (const attachment of attachments) {
    if (!(attachment.blob instanceof File)) continue
    const name = attachment.file.originalName
    try {
      const response = await expeditionAttachmentUpload(attachment.blob, {
        expedition: expeditionId,
        type: attachment.type,
        pageFormat: attachment.pageFormat,
        printPages: attachment.printPages,
      })
      if (!response.ok) {
        const message = await extractViolationMessage(response)
        failed.push({ name, message })
      }
    } catch {
      failed.push({ name })
    }
  }

  if (failed.length > 0) {
    displayToast({
      type: 'error',
      title: t({ id: 'form.attachments.uploadError', message: 'Failed to upload files' }),
      text: failed.map(formatFailedAttachment).join('; '),
    })
  }
}
